import React, { ReactElement } from 'react';
import classNames from 'classnames';
import type { TooltipProps } from '@fiverr-private/tooltip';
import { logger } from '@fiverr-private/obs';
import { LazyComponent } from '@fiverr-private/ui_utils';
import type { BaseBadgeProps } from '../../types';

import styles from './index.module.scss';

const LazyTooltip = (props: TooltipProps) => {
  const lazyImport = () => import(/* webpackChunkName: 'BadgeTooltip' */ '../BadgeTooltip');

  const onLoadError = (error: any) => {
    logger.error(error, {
      description: 'Failed to lazy-load Tooltip component',
    });
  };

  return (
    <LazyComponent<TooltipProps>
      lazyImport={lazyImport}
      shouldImport={true}
      componentProps={props}
      onError={onLoadError}
      placeholder={props.children as ReactElement}
      fallback={props.children as ReactElement}
    />
  );
};

const Badge = ({
  children,
  color,
  backgroundColor,
  tooltipContent,
  showTooltip = true,
  tooltipPosition = 'top',
  dataTestId,
  appendTooltipToBody,
  fontVariant = 'normal',
  disableTooltipFlip,
  disablePadding = false,
}: BaseBadgeProps) => {
  const props = {
    className: classNames(styles.pentaBadge, {
      [styles.allCaps]: fontVariant === 'all_caps',
      [styles.disablePadding]: disablePadding,
    }),
    style: { color, backgroundColor },
    'data-testid': dataTestId,
    children,
  };

  if (tooltipContent && showTooltip) {
    return (
      <LazyTooltip
        disableFlip={disableTooltipFlip}
        appendToBody={appendTooltipToBody}
        content={tooltipContent}
        position={tooltipPosition}
      >
        <div {...props} />
      </LazyTooltip>
    );
  }

  return <div {...props} />;
};

Badge.displayName = 'Badge';

export default Badge;
